import Service from '@/pages/dashboard/components/PageContainer/Service';
import {
  getUaRoiUaMediaScopeSelectorFingerprint,
  mergeUaRoiUaMediaScopeIntoFormattedFilters,
  type TKpiOverviewDataScope,
} from '../../utils/uaRoiUaMediaScopeFilters';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import React, { useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'umi';
import {
  attachRowDisplayNames,
  buildNaturalNewRankRequest,
  filterMetricsListHideZeroOrEmptyValue,
  formatNaturalNewRankResponse,
  metricsListToRankRows,
  NATURAL_RANK_SKIP_REQUEST_KEY,
  naturalRankBeforeRequestWithUaNetworkAllowlist,
  type INaturalRankRawRow,
  type INaturalRankReqData,
  type TNaturalRankIconMode,
} from './_utils';

const DEFAULT_API = '/api/v1/dashboard/pc/overview_daily/overall_performance';

/** KpiNaturalNewRankInner 的 props：Service 注入 data（含 rankDataKey 指向的数据桶），其他透传给 child */
interface IKpiNaturalNewRankInnerProps {
  children?: React.ReactNode;
  // Service 注入的 data 形态由上游决定：含 rankDataKey 指向的数据桶 + valueMetricKey/hideZeroOrEmptyValueMetricRows 等透传字段
  data?: Record<string, unknown>;
}

const KpiNaturalNewRankInner = (props: IKpiNaturalNewRankInnerProps) => {
  const { children, data: pageData } = props;
  const rankDataKey = (pageData?.rankDataKey as string | undefined) ?? 'naturalNewRank';
  const st = pageData?.[rankDataKey] as
    | { data?: { metrics_list?: INaturalRankRawRow[]; [key: string]: unknown; }; [key: string]: unknown; }
    | undefined;
  const payload = st?.data;
  const metricsList = (payload?.metrics_list ?? []) as INaturalRankRawRow[];
  const valueMetricKey =
    (pageData?.valueMetricKey as string | undefined) ?? 'ug_register';
  const rowIconMode: TNaturalRankIconMode =
    (pageData?.rowIconMode as TNaturalRankIconMode | undefined) ?? 'country';
  const hideZeroOrEmptyValueMetricRows = Boolean(
    pageData?.hideZeroOrEmptyValueMetricRows,
  );
  const { isEN } = useLocal();

  const metricsListForRows = useMemo(
    () =>
      filterMetricsListHideZeroOrEmptyValue(
        metricsList,
        valueMetricKey,
        hideZeroOrEmptyValueMetricRows,
      ),
    [metricsList, valueMetricKey, hideZeroOrEmptyValueMetricRows],
  );

  const rows = useMemo(() => {
    const base = metricsListToRankRows(metricsListForRows, valueMetricKey);
    return attachRowDisplayNames(base, metricsListForRows, isEN, {
      iconMode: rowIconMode,
    });
  }, [metricsListForRows, valueMetricKey, isEN, rowIconMode]);

  const mergedData = useMemo(() => {
    const next: Record<string, unknown> = {
      ...pageData,
      valueMetricKey,
    };
    if (st) {
      next[rankDataKey] = {
        ...st,
        data: {
          ...(payload && typeof payload === 'object' ? payload : {}),
          rows,
        },
      };
    }
    return next;
  }, [pageData, st, payload, rows, valueMetricKey, rankDataKey]);

  return (
    <>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
          return child;
        }
        // child props 形态由运行时决定，用 Attributes & Record<string, unknown> 承载注入字段
        const injectedProps: React.Attributes & Record<string, unknown> = {
          data: mergedData,
        };
        return React.cloneElement(child, injectedProps);
      })}
    </>
  );
};

/**
 * KpiNaturalNewRankService 的 props：外层适配组件 props（字段由注册表驱动，透传下游）
 */
interface IKpiNaturalNewRankServiceProps {
  style?: React.CSSProperties;
  className?: string;
  config?: Record<string, unknown> & {
    api?: string | ((params: Record<string, unknown>, d: unknown) => Promise<unknown>);
    beforeRequest?: string | ((data: INaturalRankReqData) => Record<string, unknown> | false);
    formatData?: string | ((data: unknown) => unknown);
    metrics?: string[];
    multi_sort?: Array<{ sort_by: string; desc?: boolean; }>;
    accumulation?: Record<string, unknown>;
    logical_flag?: string;
    page?: number;
    page_size?: number;
    valueMetricKey?: string;
    rankDataKey?: string;
    rowIconMode?: TNaturalRankIconMode;
    uaNetworkMatchType?: string[];
    uaNetworkSelectorKey?: string;
    hideZeroOrEmptyValueMetricRows?: boolean;
    applyKpiUaRoiUaMediaScope?: boolean;
    kpiOverviewDataScope?: TKpiOverviewDataScope;
  };
  children?: React.ReactNode;
  data?: Record<string, unknown>;
  api?: string | ((params: Record<string, unknown>, d: unknown) => Promise<unknown>);
  beforeRequest?: string | ((data: INaturalRankReqData) => Record<string, unknown> | false);
  formatData?: string | ((data: unknown) => unknown);
  metrics?: string[];
  multi_sort?: Array<{ sort_by: string; desc?: boolean; }>;
  accumulation?: Record<string, unknown>;
  logical_flag?: string;
  page?: number;
  page_size?: number;
  valueMetricKey?: string;
  rankDataKey?: string;
  rowIconMode?: TNaturalRankIconMode;
  uaNetworkMatchType?: string[];
  uaNetworkSelectorKey?: string;
  hideZeroOrEmptyValueMetricRows?: boolean;
  applyKpiUaRoiUaMediaScope?: boolean;
  kpiOverviewDataScope?: TKpiOverviewDataScope;
}

/** useSelector 的 state 形状由 DVA 拼装，此处仅使用局部结构类型 */
interface IKpiNaturalNewRankRootState {
  dashboard?: {
    currentGame?: {
      game_code?: string;
    };
  };
  dashboardSetting?: {
    selector?: Record<string, unknown>;
  };
}

/**
 * 自然新进排名：请求 overall_performance（logical_flag=overall_dimension_top），向 RankProgressListCard 注入 rows
 */
const KpiNaturalNewRankService = (props: IKpiNaturalNewRankServiceProps) => {
  const {
    style,
    className,
    config,
    children,
    data: propsData,
    api: apiProp,
    beforeRequest: beforeRequestProp,
    formatData: formatDataProp,
    metrics: metricsProp,
    multi_sort: multiSortProp,
    accumulation: accumulationProp,
    logical_flag: logicalFlagProp,
    page: pageProp,
    page_size: pageSizeProp,
    valueMetricKey: valueMetricKeyProp,
    rankDataKey: rankDataKeyProp,
    rowIconMode: rowIconModeProp,
    uaNetworkMatchType: uaNetworkMatchTypeProp,
    uaNetworkSelectorKey: uaNetworkSelectorKeyProp,
    hideZeroOrEmptyValueMetricRows: hideZeroOrEmptyValueMetricRowsProp,
    applyKpiUaRoiUaMediaScope: applyKpiUaRoiUaMediaScopeProp,
    kpiOverviewDataScope: kpiOverviewDataScopeProp,
  } = props || {};

  const api = apiProp ?? config?.api ?? DEFAULT_API;
  const formatData =
    formatDataProp ?? config?.formatData ?? formatNaturalNewRankResponse;

  const rankDataKey =
    rankDataKeyProp ?? config?.rankDataKey ?? 'naturalNewRank';

  const uaNetworkMatchType =
    uaNetworkMatchTypeProp ?? config?.uaNetworkMatchType;
  const uaNetworkSelectorKey =
    uaNetworkSelectorKeyProp ?? config?.uaNetworkSelectorKey ?? 'ua_network';

  const { gameCode } = useSelector(
    (state: IKpiNaturalNewRankRootState) => ({
      gameCode: state?.dashboard?.currentGame?.game_code,
    }),
    shallowEqual,
  );

  const selector = useSelector(
    (s: IKpiNaturalNewRankRootState) => s?.dashboardSetting?.selector,
  ) as Record<string, unknown> | undefined;

  const { selector: uaScopeSelector } = useSelector(
    (state: IKpiNaturalNewRankRootState) => ({
      selector: state?.dashboardSetting?.selector as
        | Record<string, Array<Record<string, unknown>>>
        | undefined,
    }),
    shallowEqual,
  );

  const applyKpiUaRoiUaMediaScope =
    applyKpiUaRoiUaMediaScopeProp ?? config?.applyKpiUaRoiUaMediaScope;

  const scopeFromData =
    propsData &&
    typeof propsData === 'object' &&
    !Array.isArray(propsData)
      ? (propsData as Record<string, unknown>).kpiOverviewDataScope as
          | TKpiOverviewDataScope
          | undefined
      : undefined;
  const kpiOverviewScope =
    kpiOverviewDataScopeProp ??
    (config?.kpiOverviewDataScope as TKpiOverviewDataScope | undefined) ??
    scopeFromData ??
    'all';

  const allowedUaNetworkCodes = useMemo(() => {
    if (!Array.isArray(uaNetworkMatchType) || uaNetworkMatchType.length === 0) {
      return undefined;
    }
    if (!selector) {
      return undefined;
    }
    const includeSet = new Set(uaNetworkMatchType.map(t => String(t)));
    const list = (selector[uaNetworkSelectorKey] || []) as Array<{
      type?: string;
      code?: string;
    }>;
    const codes = list
      .filter(item => item?.type && includeSet.has(String(item.type)))
      .map(item => item?.code)
      .filter(Boolean) as string[];
    return codes.length > 0 ? codes : undefined;
  }, [selector, uaNetworkMatchType, uaNetworkSelectorKey]);

  const data = useMemo(
    () => ({
      gameCode,
      metrics: metricsProp ?? config?.metrics ?? ['ug_register'],
      multi_sort: multiSortProp ?? config?.multi_sort,
      accumulation: accumulationProp ?? config?.accumulation,
      logical_flag: logicalFlagProp ?? config?.logical_flag,
      page: pageProp ?? config?.page,
      page_size: pageSizeProp ?? config?.page_size,
      valueMetricKey: valueMetricKeyProp ?? config?.valueMetricKey ?? 'ug_register',
      rankDataKey,
      rowIconMode: rowIconModeProp ?? config?.rowIconMode,
      uaNetworkMatchType,
      allowedUaNetworkCodes,
      hideZeroOrEmptyValueMetricRows: Boolean(
        hideZeroOrEmptyValueMetricRowsProp ??
          config?.hideZeroOrEmptyValueMetricRows,
      ),
      ...(propsData || {}),
      ...(applyKpiUaRoiUaMediaScope
        ? { _kpiOverviewScopeToken: kpiOverviewScope }
        : {}),
    }),
    [
      gameCode,
      metricsProp,
      multiSortProp,
      accumulationProp,
      logicalFlagProp,
      pageProp,
      pageSizeProp,
      valueMetricKeyProp,
      rankDataKey,
      rowIconModeProp,
      config,
      propsData,
      uaNetworkMatchType,
      allowedUaNetworkCodes,
      hideZeroOrEmptyValueMetricRowsProp,
      applyKpiUaRoiUaMediaScope,
      kpiOverviewScope,
    ],
  );

  const resolveBuildBase = useCallback((): ((
    d: INaturalRankReqData,
  ) => Record<string, unknown> | false) => {
    if (typeof beforeRequestProp === 'function') {
      return beforeRequestProp;
    }
    if (typeof beforeRequestProp === 'string') {
      // 字符串配置通过 stringToFunction 运行时解析，签名由上游约定
      return stringToFunction(beforeRequestProp) as (
        d: INaturalRankReqData,
      ) => Record<string, unknown> | false;
    }
    if (typeof config?.beforeRequest === 'function') {
      return config.beforeRequest;
    }
    if (typeof config?.beforeRequest === 'string') {
      return stringToFunction(config.beforeRequest) as (
        d: INaturalRankReqData,
      ) => Record<string, unknown> | false;
    }
    return buildNaturalNewRankRequest;
  }, [beforeRequestProp, config?.beforeRequest]);

  const beforeRequest = useCallback(
    (payload: INaturalRankReqData) => {
      const inner = naturalRankBeforeRequestWithUaNetworkAllowlist(
        payload,
        resolveBuildBase(),
      );
      if (inner === false) {
        return false;
      }
      if (
        inner &&
        typeof inner === 'object' &&
        inner[NATURAL_RANK_SKIP_REQUEST_KEY]
      ) {
        return inner;
      }
      if (
        !applyKpiUaRoiUaMediaScope ||
        kpiOverviewScope !== 'ua_roi_ua_media'
      ) {
        return inner;
      }
      const filters = inner.filters as Record<string, unknown> | undefined;
      if (!filters || typeof filters !== 'object') {
        return inner;
      }
      const merged = mergeUaRoiUaMediaScopeIntoFormattedFilters(
        filters,
        payload.filterVal as Record<string, unknown> | undefined,
        uaScopeSelector,
      );
      if (merged === false) {
        return { [NATURAL_RANK_SKIP_REQUEST_KEY]: true };
      }
      return { ...inner, filters: merged };
    },
    [
      applyKpiUaRoiUaMediaScope,
      kpiOverviewScope,
      resolveBuildBase,
      uaScopeSelector,
    ],
  );

  const trackDataStable = useMemo(() => {
    const base = [
      'gameCode',
      'metrics',
      'multi_sort',
      'accumulation',
      'logical_flag',
      'page',
      'page_size',
      'valueMetricKey',
      'rankDataKey',
      'rowIconMode',
      'uaNetworkMatchType',
      'allowedUaNetworkCodes',
      'hideZeroOrEmptyValueMetricRows',
    ];
    if (applyKpiUaRoiUaMediaScope) {
      return [...base, '_kpiOverviewScopeToken'];
    }
    return base;
  }, [applyKpiUaRoiUaMediaScope]);

  const serviceMountKey = useMemo(() => {
    if (!applyKpiUaRoiUaMediaScope) {
      return 'kpi-natural-new-rank';
    }
    if (kpiOverviewScope !== 'ua_roi_ua_media') {
      return `kpi-natural-new-rank-${kpiOverviewScope}`;
    }
    return `kpi-natural-new-rank-${kpiOverviewScope}-${getUaRoiUaMediaScopeSelectorFingerprint(
      uaScopeSelector,
    )}`;
  }, [applyKpiUaRoiUaMediaScope, kpiOverviewScope, uaScopeSelector]);

  const wrappedApi = useMemo(() => {
    const empty = () => Promise.resolve({ metrics_list: [] as unknown[] });
    if (typeof api === 'function') {
      return (params: Record<string, unknown>, d: unknown) => {
        if (params?.[NATURAL_RANK_SKIP_REQUEST_KEY]) {
          return empty();
        }
        return (api as (p: Record<string, unknown>, x: unknown) => Promise<unknown>)(
          params,
          d,
        );
      };
    }
    const url = String(api);
    return (params: Record<string, unknown>) => {
      if (params?.[NATURAL_RANK_SKIP_REQUEST_KEY]) {
        return empty();
      }
      return dashboardRequest({
        url,
        method: 'post',
        data: params,
      });
    };
  }, [api]);

  return (
    <div className={className} style={style}>
      <Service
        key={serviceMountKey}
        // Service.api 签名为 string | ((data, outParame?) => void)，此处 wrappedApi 为 (params, d?) => Promise，运行时兼容，此处断言对齐
        api={wrappedApi as string | ((data: unknown, outParame?: unknown) => void)}
        data={data}
        dataKey={rankDataKey as string}
        trackData={trackDataStable}
        beforeRequest={beforeRequest}
        formatData={formatData as string | ((data: unknown, filterVal?: unknown) => unknown)}
      >
        <KpiNaturalNewRankInner>
          {children}
        </KpiNaturalNewRankInner>
      </Service>
    </div>
  );
};

export default KpiNaturalNewRankService;
